/**
 * Exposure tracker — Phase 4.
 *
 * Batches impression + click events from the storefront and posts them to
 * /api/public/exposure/track. Used to power admin analytics and
 * intelligent rotation in curated sections.
 *
 * Usage:
 *   import { trackImpression, trackClick, useImpressionTracker } from "@/lib/exposure";
 *
 *   // 1) On a card mount that may be off-screen, observe with the hook:
 *   const ref = useImpressionTracker({ kind: "product", ref_id: id, section });
 *   return <div ref={ref}>...</div>;
 *
 *   // 2) On click:
 *   trackClick({ kind: "product", ref_id: id, section });
 */
import { useEffect, useRef } from "react";

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/public/exposure/track`;

// Anonymous session id (persisted in sessionStorage so it survives reloads
// within the same tab but resets across tabs/sessions).
function getSessionId() {
  try {
    let sid = sessionStorage.getItem("apc_exposure_sid");
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem("apc_exposure_sid", sid);
    }
    return sid;
  } catch {
    return null;
  }
}

function getVendorRef() {
  try { return localStorage.getItem("alendra_referrer") || null; } catch { return null; }
}

// In-session dedup so a single page-view doesn't fire 10 impressions for
// the same card. Cleared by reload.
const seen = new Set();
let buffer = [];
let flushTimer = null;

function flush() {
  if (!buffer.length) return;
  const events = buffer;
  buffer = [];
  flushTimer = null;
  try {
    const body = JSON.stringify({ events });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
    } else {
      fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch { /* ignore */ }
}

function enqueue(ev) {
  buffer.push(ev);
  if (buffer.length >= 25) {
    flush();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(flush, 2500);
  }
}

// Flush pending events when the page becomes hidden / unloaded.
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
  window.addEventListener("pagehide", flush);
}

export function trackImpression({ kind, ref_id, section }) {
  if (!kind || !ref_id) return;
  const key = `imp:${kind}:${ref_id}:${section || ""}`;
  if (seen.has(key)) return;
  seen.add(key);
  enqueue({
    kind, ref_id, event: "impression",
    section: section || null,
    vendor_ref: getVendorRef(),
    session_id: getSessionId(),
  });
}

export function trackClick({ kind, ref_id, section }) {
  if (!kind || !ref_id) return;
  enqueue({
    kind, ref_id, event: "click",
    section: section || null,
    vendor_ref: getVendorRef(),
    session_id: getSessionId(),
  });
}

/**
 * Returns a ref that fires `trackImpression` the first time the element
 * intersects the viewport (>=40% visible). Safe to call with stable args.
 */
export function useImpressionTracker({ kind, ref_id, section }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !kind || !ref_id) return undefined;
    const el = ref.current;
    let cancelled = false;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !cancelled) {
            trackImpression({ kind, ref_id, section });
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => { cancelled = true; obs.disconnect(); };
  }, [kind, ref_id, section]);
  return ref;
}
